"use client";
// Updated 2026-05-06 with Option B conservative defaults

import { useState } from "react";
import { useScrollAnimate } from "../lib/hooks";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const RED = "#ef4444";
const RED_BADGE = "#f87171";
const AMBER = "#f59e0b";
const CARD_BG = "rgba(255,255,255,0.03)";
const CARD_BD = "rgba(255,255,255,0.06)";

const DEFAULTS = {
  spend: 20000,
  trials: 160,
  revenue: 0,
  closeRate: 12,
  acv: 7200,
  realization: 50,
  cannibalization: 12,
  liftValue: 14400,
};

const fmt = (n) => Math.round(n).toLocaleString("en-US");
const fmtROAS = (n) => {
  if (!isFinite(n) || n < 0) return "0.0x";
  if (n > 999) return "999+x";
  return `${n.toFixed(1)}x`;
};

const verdict = (roas) => {
  if (!isFinite(roas) || roas < 1) return { label: "KILL", color: RED, labelColor: RED_BADGE, bg: "rgba(239,68,68,0.12)" };
  if (roas < 3) return { label: "HOLD", color: AMBER, labelColor: AMBER, bg: "rgba(245,158,11,0.12)" };
  return { label: "SCALE", color: TEAL, labelColor: TEAL, bg: "rgba(79,205,196,0.12)" };
};

export default function ChatGPTAdsROASCalculator() {
  const [ref, scrolled] = useScrollAnimate();
  const [inputs, setInputs] = useState(DEFAULTS);

  const update = (key, value) => {
    const num = Math.max(0, Number(value) || 0);
    setInputs((prev) => ({ ...prev, [key]: num }));
  };

  const reset = () => setInputs(DEFAULTS);

  const { spend, trials, revenue, closeRate, acv, realization, cannibalization, liftValue } = inputs;

  const layer1 = spend > 0 ? revenue / spend : 0;
  const pipelineValue = trials * (closeRate / 100) * acv * (realization / 100);
  const layer2 = spend > 0 ? pipelineValue / spend : 0;
  const incrementalTrials = trials * (1 - cannibalization / 100);
  const incrementalPipeline = incrementalTrials * (closeRate / 100) * acv * (realization / 100);
  const liftContribution = spend > 0 ? liftValue / spend : 0;
  const layer3 = (spend > 0 ? incrementalPipeline / spend : 0) + liftContribution;

  const v1 = verdict(layer1);
  const v2 = verdict(layer2);
  const v3 = verdict(layer3);

  return (
    <div
      ref={ref}
      style={{
        margin: "32px 0",
        opacity: scrolled ? 1 : 0,
        transform: scrolled ? "translateY(0)" : "translateY(8px)",
        transition: "opacity 0.5s ease, transform 0.5s ease",
        borderRadius: 14,
        border: `1px solid ${CARD_BD}`,
        background: "rgba(255,255,255,0.02)",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${CARD_BD}` }}>
        <div style={{ fontFamily: "var(--display)", fontSize: 11, fontWeight: 700, color: TEAL, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>
          Interactive Calculator
        </div>
        <div style={{ fontFamily: "var(--display)", fontSize: 19, fontWeight: 700, color: TEXT, lineHeight: 1.3 }}>
          ChatGPT Ads ROAS Stack Calculator
        </div>
        <div style={{ fontFamily: "var(--body)", fontSize: 14, color: TEXT_DIM, marginTop: 6, lineHeight: 1.5 }}>
          Plug in your numbers. The Stack returns Layer 1, 2, and 3 ROAS with a verdict for each. Defaults are loaded with the worked example from this post.
        </div>
      </div>

      <div
        style={{
          padding: "20px 24px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
        }}
      >
        <InputField label="Spend ($)" value={spend} onChange={(v) => update("spend", v)} prefix="$" />
        <InputField label="Trials generated" value={trials} onChange={(v) => update("trials", v)} />
        <InputField label="Revenue at measurement ($)" value={revenue} onChange={(v) => update("revenue", v)} prefix="$" />
        <InputField label="Trial → paid close rate" value={closeRate} onChange={(v) => update("closeRate", v)} suffix="%" />
        <InputField label="Average ACV ($)" value={acv} onChange={(v) => update("acv", v)} prefix="$" />
        <InputField label="Pipeline realization rate" value={realization} onChange={(v) => update("realization", v)} suffix="%" />
        <InputField label="Cannibalization rate" value={cannibalization} onChange={(v) => update("cannibalization", v)} suffix="%" />
        <InputField label="12-month AI Visibility Lift value ($)" value={liftValue} onChange={(v) => update("liftValue", v)} prefix="$" />
      </div>

      <div style={{ padding: "0 24px 18px" }}>
        <button
          onClick={reset}
          type="button"
          style={{
            background: "transparent",
            border: `1px solid ${CARD_BD}`,
            color: TEXT_DIM,
            fontFamily: "var(--body)",
            fontSize: 13,
            padding: "8px 16px",
            borderRadius: 8,
            cursor: "pointer",
            fontWeight: 500,
          }}
          aria-label="Reset calculator inputs to the worked-example defaults"
        >
          ← Reset to worked-example defaults
        </button>
      </div>

      <div
        style={{
          padding: "22px 24px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
          background: "rgba(0,0,0,0.22)",
          borderTop: `1px solid ${CARD_BD}`,
        }}
      >
        <LayerCard num="1" name="Click ROAS" value={layer1} verdict={v1} note="Revenue ÷ Spend" />
        <LayerCard num="2" name="Pipeline ROAS" value={layer2} verdict={v2} note="Trials × close × ACV × realization" />
        <LayerCard num="3" name="Bridge ROAS" value={layer3} verdict={v3} note="Incremental pipeline + Visibility Lift" />
      </div>

      <div style={{ padding: "14px 24px", borderTop: `1px solid ${CARD_BD}`, fontFamily: "var(--body)", fontSize: 12, color: TEXT_DIM, lineHeight: 1.55 }}>
        Verdict thresholds: <span style={{ color: RED, fontWeight: 600 }}>KILL</span> at &lt;1.0x · <span style={{ color: AMBER, fontWeight: 600 }}>HOLD</span> at 1.0–3.0x · <span style={{ color: TEAL, fontWeight: 600 }}>SCALE</span> at &gt;3.0x. Layer 4 (Blended Brand ROAS) is omitted from this calculator and should be modeled separately for enterprise teams with mature attribution.
      </div>
    </div>
  );
}

function InputField({ label, value, onChange, prefix, suffix }) {
  const inputId = `roas-calc-${label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`;
  return (
    <div>
      <label
        htmlFor={inputId}
        style={{
          display: "block",
          fontFamily: "var(--body)",
          fontSize: 12,
          color: TEXT_DIM,
          marginBottom: 6,
          fontWeight: 500,
        }}
      >
        {label}
      </label>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          background: CARD_BG,
          border: `1px solid ${CARD_BD}`,
          borderRadius: 8,
        }}
      >
        {prefix && (
          <span style={{ paddingLeft: 12, color: TEXT_DIM, fontFamily: "var(--mono)", fontSize: 14 }}>{prefix}</span>
        )}
        <input
          id={inputId}
          type="number"
          min="0"
          step="any"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            flex: 1,
            background: "transparent",
            border: "none",
            outline: "none",
            color: TEXT,
            fontFamily: "var(--mono)",
            fontSize: 15,
            padding: prefix || suffix ? "10px 6px" : "10px 12px",
            width: "100%",
            minWidth: 0,
          }}
        />
        {suffix && (
          <span style={{ paddingRight: 12, color: TEXT_DIM, fontFamily: "var(--mono)", fontSize: 14 }}>{suffix}</span>
        )}
      </div>
    </div>
  );
}

function LayerCard({ num, name, value, verdict, note }) {
  return (
    <div
      style={{
        padding: "16px 18px",
        borderRadius: 10,
        background: CARD_BG,
        border: `1px solid ${CARD_BD}`,
      }}
    >
      <div
        style={{
          fontFamily: "var(--display)",
          fontSize: 11,
          fontWeight: 700,
          color: TEXT_DIM,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        Layer {num}
      </div>
      <div style={{ fontFamily: "var(--display)", fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 12 }}>
        {name}
      </div>
      <div
        style={{
          fontFamily: "var(--display)",
          fontSize: 30,
          fontWeight: 800,
          color: verdict.color,
          marginBottom: 8,
          lineHeight: 1,
        }}
      >
        {fmtROAS(value)}
      </div>
      <div
        style={{
          display: "inline-block",
          padding: "3px 10px",
          borderRadius: 6,
          background: verdict.bg,
          fontFamily: "var(--body)",
          fontSize: 11,
          fontWeight: 700,
          color: verdict.labelColor || verdict.color,
          marginBottom: 10,
          letterSpacing: "0.06em",
        }}
      >
        {verdict.label}
      </div>
      <div style={{ fontFamily: "var(--body)", fontSize: 11, color: TEXT_DIM, lineHeight: 1.55 }}>{note}</div>
    </div>
  );
}
